// -----------------------------------------------------------------------
// <copyright company="Microsoft Corporation">
// Copyright (c) Microsoft Corporation. All rights reserved.
// </copyright>
// -----------------------------------------------------------------------
import type { ErrorLevel, ErrorScenarios, KnownErrors } from '../types/external';
import { isUnsupportedBrowserError } from './unsupportedBrowserUtil';

// Not using IEngineError interface due to dependency issues.
interface IErrorWithExpected {
  isExpected: boolean;
  errorName: string;
}

/**
 * This class represents an error from OnePlayer. It is implemented as a class so that catch blocks can use
 * instanceof to know that the error is a OnePlayerError object.
 */
export class OnePlayerError {
  /**
   * The level of the error. This is used to determine whether the error is fatal or not.
   */
  public readonly level: ErrorLevel;

  /**
   * Whether the error is expected or not.
   */
  public readonly isExpected: boolean;

  /**
   * The scenario the error occurred in.
   */
  public readonly scenario: ErrorScenarios;

  /**
   * If this a known, specific, error, it will be listed here. Otherwise this will be undefined.
   */
  public readonly knownError: KnownErrors;

  /**
   * The original error that was thrown, if applicable.
   */
  public readonly originalError: unknown | undefined;

  /**
   * An optional message to go along with the error. NOTE: It is not guaranteed to be safe to log to
   * telemetry, because it is not scrubbed for user data. Hosts should scrub this message before logging it.
   */
  public readonly message: string | undefined;

  /**
   * The playbackSessionId of the OnePlayer session which can be used to correlate with the OnePlayer
   * telemetry for further analysis. Not readonly so that we can populate it at the top of the stack rather
   * than needing to pass the ids all the way up the stack.
   */
  public playbackSessionId: string | undefined;

  /**
   * The hostSessionId passed to OnePlayer which can be used to correlate with the OnePlayer telemetry for
   * further analysis. Not readonly so that we can populate it at the top of the stack rather than needing to
   * pass the ids all the way up the stack.
   */
  public hostSessionId: string | undefined;

  // To be used when the isExpected flag is known.
  public static createKnownError(
    level: ErrorLevel,
    isExpected: boolean,
    scenario: ErrorScenarios,
    knownError: KnownErrors,
    originalError: unknown,
    message?: string,
    playbackSessionId?: string,
    hostSessionId?: string
  ): OnePlayerError {
    return new OnePlayerError(
      level,
      isExpected,
      scenario,
      knownError,
      originalError,
      message,
      playbackSessionId,
      hostSessionId
    );
  }

  // To be used when the isExpected flag unknown.
  public static createGenericError(
    level: ErrorLevel,
    scenario: ErrorScenarios,
    knownError: KnownErrors,
    originalError: unknown,
    message?: string,
    playbackSessionId?: string,
    hostSessionId?: string
  ): OnePlayerError {
    let isExpected = false;
    // check if its engine error, not using original interface due to dependency issues.
    if ((originalError as IErrorWithExpected)?.isExpected !== undefined) {
      isExpected = (originalError as IErrorWithExpected).isExpected;
      message = (originalError as IErrorWithExpected).errorName;
    } else {
      if (originalError && (originalError as Error).message) {
        const isExpectedUnsupportedBrowserError: boolean = isUnsupportedBrowserError(
          (originalError as Error).message
        );
        if (isExpectedUnsupportedBrowserError) {
          isExpected = true;
        }
      }
    }
    return OnePlayerError.createKnownError(
      level,
      isExpected,
      scenario,
      knownError,
      originalError,
      message,
      playbackSessionId,
      hostSessionId
    );
  }

  // IMPORTANT PLEASE READ: There is a copy of this file in the WebVideo package. If you are
  // updating this file, you MUST also update the file in the WebVideo package. We plan to merge the
  // two files and have both packages reference shared package eventually, tracked by PBI 1685541:
  // https://dev.azure.com/onedrive/OMG/_workitems/edit/1685541
  private constructor(
    level: ErrorLevel,
    isExpected: boolean,
    scenario: ErrorScenarios,
    knownError: KnownErrors,
    originalError: unknown,
    message?: string,
    playbackSessionId?: string,
    hostSessionId?: string
  ) {
    this.level = level;
    this.isExpected = isExpected;
    this.scenario = scenario;
    this.knownError = knownError;
    this.originalError = originalError;
    this.message = message;
    this.playbackSessionId = playbackSessionId;
    this.hostSessionId = hostSessionId;
  }
}
